import { cn } from '@/lib/utils';

/**
 * Small segmented toggle used for the NET/GROSS switch and the day/week/month
 * granularity switch (docs/UI_STANDARDS — one control, reused; never reinvented
 * per view). Pure + controlled: it renders options and reports the chosen value.
 */
export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Optional per-option active styling (e.g. the NETO teal / BRUTO vatmode tint
   * from the approved canvas). Falls back to the default primary fill. */
  activeClass?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Grays out and inert-ifies the whole control WITHOUT removing it from the
   * chrome — used when a scope is temporarily meaningless (e.g. the prepayment
   * scope under the accrual basis) so the header layout never jumps. */
  disabled?: boolean;
  /** Native tooltip explaining WHY the control is disabled. */
  disabledTitle?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  disabledTitle,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      title={disabled ? disabledTitle : undefined}
      className={cn(
        'inline-flex rounded-md border border-border bg-card p-0.5',
        disabled && 'opacity-40',
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors',
              disabled
                ? selected
                  ? 'cursor-not-allowed bg-muted text-muted-foreground'
                  : 'cursor-not-allowed text-muted-foreground'
                : selected
                  ? option.activeClass ?? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
